import { useCallback, useEffect, useState } from 'react';
import { mixgenieServiceURL } from '../redux/services/defines';

/**
 * Web Push subscribe/unsubscribe hook. Consumers get:
 *   - supported:  browser has the required APIs (Notification + PushManager + SW)
 *   - permission: 'default' | 'granted' | 'denied' (mirror of Notification.permission)
 *   - subscribed: boolean — is there an active PushSubscription on this device
 *   - subscribe(): request permission + register with the SW + POST to backend
 *   - unsubscribe(): unsubscribe the SW + DELETE on backend
 *   - error: last error message, if any
 *
 * The VAPID public key must be exposed to the client at build time via
 * VITE_VAPID_PUBLIC_KEY (base64 urlsafe). No key = subscribe() throws.
 * iOS Safari supports Web Push only for PWAs installed to home screen
 * (iOS 16.4+) — desktop iOS Safari always returns unsupported.
 */
export function usePushSubscription() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const isSupported =
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window;
    setSupported(isSupported);
    if (!isSupported) return;
    setPermission(Notification.permission);

    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscribed(!!sub);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      if (!supported) throw new Error('Push notifications aren\'t supported on this device');
      const perm = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Notification permission was not granted');

      const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
      if (!publicKey) throw new Error('Push isn\'t configured — VAPID key missing');

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const payload = sub.toJSON();
      // The gateway forwards this to mixgenie-service via main-service
      // authenticated gateway — cookies are attached automatically.
      const url = `${mixgenieServiceURL}/user/push/subscribe`;
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: payload.endpoint,
          keys: payload.keys,
        }),
      });
      if (!res.ok) throw new Error(`Register failed (${res.status})`);
      setSubscribed(true);
    } catch (e: any) {
      setError(e?.message || 'Subscribe failed');
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await fetch(`${mixgenieServiceURL}/user/push/subscribe`, {
            method: 'DELETE',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ endpoint }),
          });
        } catch { /* silent; local unsubscribe already worked */ }
      }
      setSubscribed(false);
    } catch (e: any) {
      setError(e?.message || 'Unsubscribe failed');
    } finally {
      setBusy(false);
    }
  }, []);

  return { supported, permission, subscribed, busy, error, subscribe, unsubscribe };
}

/**
 * VAPID keys are base64-urlsafe strings; PushManager.subscribe wants a
 * Uint8Array of raw bytes. Standard conversion.
 */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}
